<script>
import PhoenixForm from '@phoenix/mixins/components/form/PhoenixForm';
import ChinesePIPL from '@maison/components/form/fields/ChinesePIPL';
import KoreanGDPR from '@maison/components/form/KoreanGDPR';
import { useForm } from 'vee-validate';

export default {
  components: {
    ChinesePIPL,
    KoreanGDPR,
  },

  mixins: [PhoenixForm],

  props: {
    isContained: {
      type: Boolean,
      default: true,
    },
    customSubmitButtonClasses: {
      type: String,
      default: null,
    },
    customOuterClass: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { setValues, validate } = useForm();
    return { setValues, validate };
  },

  computed: {
    submitClass() {
      return this.customSubmitButtonClasses || 'sgphx-btn sgphx-btn--primary sgphx-btn--positive';
    },

    cancelClass() {
      return ['sgphx-btn sgphx-btn--primary'];
    },

    hasFormActionsSlot() {
      return !!this.$slots['form-actions'];
    },

    isLastStepOnMultipleStepsForm() {
      return !this.hasSteps || this.isFinalStep;
    },

    shouldDisplayPIPLConsentCheckboxes() {
      return (
        this.shouldDisplayChinesePIPL && this.allowedForms.includes(this.formType) && this.isLastStepOnMultipleStepsForm
      );
    },
  },
};
</script>

<template>
  <form ref="form" @submit.prevent="submit">
    <div v-if="isLoading" class="text-center">
      <div class="loader"></div>
    </div>

    <div v-show="!isLoading">
      <div :class="[{ container: isContained }]">
        <slot :should-display-newsletter="!shouldDisplayKoreanGDPR" />
      </div>

      <div class="sgphx">
        <div class="sgphx-theme-light">
          <korean-g-d-p-r
            v-if="shouldDisplayKoreanGDPR"
            :initial-values="formValues"
            :form-type="koreanFormType"
            :custom-outer-class="customOuterClass"
            @change="onKoreanGDPRChange"
            @consent-values-loaded="onConsentValuesLoaded"
          />

          <chinese-p-i-p-l
            v-if="shouldDisplayPIPLConsentCheckboxes"
            :value="chinesePiplConsents"
            :custom-inner-class="customConsentInnerClass"
            :custom-outer-class="customConsentOuterClass"
            :privacy-link="terms"
            :form-type="formType"
            @on-chinese-pipl-consent-change="onChinesePiplConsentChange"
          />
        </div>
      </div>

      <div class="sgphx">
        <div v-if="!showErrorsInToast && mainErrors" class="sgphx-mt-md">
          <div class="sgphx-form__feedback sgphx-feedback--danger">
            {{ mainErrors }}
          </div>
        </div>
      </div>

      <div class="sgphx">
        <div
          v-if="shouldShowFormActions"
          :class="['sgphx-form-actions', { 'sgphx-form-actions--divided': isContained }]"
        >
          <ul class="sgphx-form-actions__items" role="presentation">
            <li v-if="hasFormActionsSlot" role="presentation">
              <slot name="form-actions" :is-disabled="isDisabled" />
            </li>

            <li v-if="shouldDisplayPreviousButton" role="presentation">
              <div>
                <button class="sgphx-btn sgphx-btn--underline" type="button" @click.prevent="goToPreviousStep">
                  <span class="sgphx-btn__text">
                    <span>{{ previousStepText }}</span>
                  </span>
                </button>
              </div>
            </li>

            <li v-if="shouldDisplayCancelButton" role="presentation">
              <div>
                <button
                  ref="cancelButton"
                  :disabled="isDisabled"
                  :class="cancelClass"
                  data-cy="form-cancel-btn"
                  @click.prevent="onCancelCallback"
                >
                  <span class="sgphx-btn__text">
                    <span>{{ cancelText }}</span>
                  </span>
                </button>
              </div>
            </li>

            <li v-if="shouldDisplaySubmitButton" role="presentation">
              <div>
                <button
                  ref="submitButton"
                  :disabled="isDisabled"
                  :class="submitClass"
                  type="submit"
                  data-cy="form-submit-btn"
                >
                  <span class="sgphx-btn__text">
                    <span>{{ submitText }}</span>
                  </span>
                </button>
              </div>
            </li>
          </ul>

          <p v-if="shouldDisplayTermsLink" class="sgphx-form-action__text" v-html="termsLink" />

          <p
            v-if="shouldDisplayPIPLStatement"
            class="sgphx-form-action__text"
            data-cy="pipl-statement"
            v-html="piplStatement"
          />
        </div>
      </div>
    </div>
  </form>
</template>
